import { useMemo } from "react";

import { AgeingBar, MiniColumns, OpposingBar } from "../components/charts.js";
import { Card, Empty, Money, Row, Rows, SectionTitle, Stat } from "../components/ui.js";
import {
  deriveDashboard,
  deriveDayBook,
  deriveSalesRegister,
  formatDate,
  formatMoney,
} from "../derive/reports.js";
import { AGEING_BUCKETS, deriveOutstanding } from "../derive/outstanding.js";
import { Delta } from "../components/PeriodPicker.js";
import { deriveMonthOnMonth } from "../derive/insights.js";
import { navigate } from "../routes.js";
import { useApp } from "../state.js";

export function Dashboard() {
  const { snapshot, groups, asOf } = useApp();

  const model = useMemo(() => {
    if (!snapshot || !groups) return null;
    const dashboard = deriveDashboard(snapshot, asOf, groups);
    const outstanding = deriveOutstanding(snapshot, asOf, groups);
    const sales = deriveSalesRegister(snapshot, groups);
    return {
      dashboard,
      outstanding,
      // Oldest month first so the chart reads left to right in time.
      months: sales.months.slice(0, 6).reverse(),
      recent: deriveDayBook(snapshot).slice(0, 5),
      mom: deriveMonthOnMonth(snapshot, asOf, groups),
    };
  }, [snapshot, groups, asOf]);

  if (!model) return <Empty>Nothing loaded.</Empty>;
  const { dashboard, outstanding, months, recent, mom } = model;

  const inHand = dashboard.cash + dashboard.bank;
  const overdue = outstanding.receivableBuckets["61-90"] + outstanding.receivableBuckets["90+"];
  const ageing = AGEING_BUCKETS.map((b) => ({
    label: b === "90+" ? "90+ days" : `${b} days`,
    value: outstanding.receivableBuckets[b],
  }));

  return (
    <div className="space-y-5">
      {/* Hero. One number answers "how am I doing" before anything else loads
          into the reader's head. */}
      <section
        className="rounded-2xl border border-transparent bg-accent-wash px-5 py-5"
        style={{ boxShadow: "var(--shadow-card)" }}
      >
        <div className="text-[11px] font-semibold uppercase tracking-[0.09em] text-ink-3">
          Cash and bank in hand
        </div>
        <div className="mt-1 text-[34px] font-semibold leading-none tracking-tight text-ink">
          {formatMoney(inHand)}
        </div>
        <div className="mt-4">
          <OpposingBar
            left={outstanding.totalReceivable}
            right={outstanding.totalPayable}
            leftLabel="owed to you"
            rightLabel="you owe"
          />
        </div>
      </section>

      <div className="grid grid-cols-2 gap-3">
        <Stat
          label="Cash"
          value={formatMoney(dashboard.cash)}
          onClick={() => navigate({ id: "cash-book" })}
        />
        <Stat
          label="Bank"
          value={formatMoney(dashboard.bank)}
          onClick={() => navigate({ id: "cash-book" })}
        />
      </div>

      <div>
        <SectionTitle
          action={
            <button
              type="button"
              className="text-xs font-medium text-accent"
              onClick={() => navigate({ id: "outstanding" })}
            >
              All parties
            </button>
          }
        >
          Receivables by age
        </SectionTitle>
        <Card>
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <span className="text-[26px] font-semibold leading-none tracking-tight text-ink">
              {formatMoney(outstanding.totalReceivable)}
            </span>
            <span className="text-xs text-ink-3">
              {outstanding.receivables.length} parties
            </span>
          </div>
          <AgeingBar data={ageing} total={outstanding.totalReceivable} />
          {overdue > 0 && (
            // Status colour, but never colour alone — it ships with a label.
            <p className="mt-3 flex items-start gap-2 rounded-xl bg-bad-wash px-3 py-2 text-xs text-bad">
              <svg viewBox="0 0 24 24" className="mt-px h-4 w-4 shrink-0" fill="currentColor" aria-hidden>
                <path d="M12 2 1 21h22L12 2Zm1 15h-2v-2h2v2Zm0-4h-2V9h2v4Z" />
              </svg>
              <span>
                <strong className="font-semibold">{formatMoney(overdue)}</strong> is more than 60
                days overdue.
              </span>
            </p>
          )}
        </Card>
      </div>

      <div>
        <SectionTitle
          action={
            <button
              type="button"
              className="text-xs font-medium text-accent"
              onClick={() => navigate({ id: "sales" })}
            >
              Register
            </button>
          }
        >
          Sales by month
        </SectionTitle>
        <Card>
          {mom && (
            <div className="mb-3 flex items-baseline justify-between gap-3">
              <span className="text-[22px] font-semibold leading-none tracking-tight text-ink">
                {formatMoney(mom.thisMonth)}
              </span>
              <span className="flex items-baseline gap-1.5 text-xs text-ink-3">
                {mom.lastLabel} {formatMoney(mom.lastMonth)}
                <Delta percent={mom.changePercent} />
              </span>
            </div>
          )}
          {/*
            Say when the month is incomplete. Without it every reading before
            the 28th looks like a collapse against a full previous month, and
            somebody eventually acts on that.
          */}
          {mom?.partialMonth && (
            <p className="mb-3 text-xs text-ink-3">
              {mom.thisLabel} is {mom.dayOfMonth} of {mom.daysInMonth} days so far.
            </p>
          )}
          <MiniColumns data={months.map((m) => ({ label: m.label, value: m.amount }))} />
        </Card>
      </div>

      <div>
        <SectionTitle
          action={
            <button
              type="button"
              className="text-xs font-medium text-accent"
              onClick={() => navigate({ id: "daybook" })}
            >
              Day Book
            </button>
          }
        >
          Recent vouchers
        </SectionTitle>
        <Card flush>
          {recent.length === 0 ? (
            <Empty>No vouchers in this snapshot.</Empty>
          ) : (
            <Rows>
              {recent.map((entry) => (
                <Row
                  key={entry.id}
                  avatar
                  title={entry.party || entry.voucherType}
                  sub={`${entry.voucherType} ${entry.voucherNumber} · ${formatDate(entry.date)}`}
                  right={<Money amount={entry.amount} />}
                  onClick={() => navigate({ id: "voucher", param: entry.id })}
                />
              ))}
            </Rows>
          )}
        </Card>
      </div>
    </div>
  );
}
